import type { TablerIcon } from "@tabler/icons-react";
import {
  IconAntenna,
  IconAtom,
  IconBatteryAutomotive,
  IconBolt,
  IconBox,
  IconBrain,
  IconBriefcase,
  IconBuildingBank,
  IconBuildingFactory2,
  IconBuildingSkyscraper,
  IconBuildingStore,
  IconCalendarEvent,
  IconCar,
  IconChargingPile,
  IconCloudComputing,
  IconCpu,
  IconCpu2,
  IconCrane,
  IconDatabase,
  IconDeviceTv,
  IconDevices,
  IconDiamond,
  IconDna,
  IconDrone,
  IconDroplet,
  IconFish,
  IconFlame,
  IconFlask,
  IconGasStation,
  IconHammer,
  IconHeartHandshake,
  IconHeartRateMonitor,
  IconLock,
  IconMicroscope,
  IconPackage,
  IconPaw,
  IconPick,
  IconPill,
  IconPlane,
  IconPrinter,
  IconRecycle,
  IconRobot,
  IconRocket,
  IconSatellite,
  IconScale,
  IconSchool,
  IconSettingsAutomation,
  IconShield,
  IconShip,
  IconShirt,
  IconSofa,
  IconSolarPanel2,
  IconSparkles,
  IconSpeakerphone,
  IconStethoscope,
  IconTopologyStar3,
  IconTractor,
  IconTrain,
  IconTree,
  IconTruckDelivery,
  IconUmbrella,
  IconUsers,
  IconVaccine,
  IconWind,
  IconWorld,
} from "@tabler/icons-react";
import { getSectorHref, getSectorSlugByIndustry, HOME_SECTOR_CARDS } from "@/pages/home/sectorCatalog";
import {
  defaultSectorVisual,
  getSectorIcon,
  getSectorVisual,
  sectorIconBySlug,
  type SectorCardVisual,
} from "@/pages/home/sectorCardVisual.util";

export type FocusSectorIconOption = {
  id: string;
  Icon: TablerIcon;
  label: string;
};

/** Extra short-id icons (stored on the focus sector). All ids stay under 40 chars. */
const EXTRA_FOCUS_SECTOR_OPTIONS: FocusSectorIconOption[] = [
  { id: "factory", Icon: IconBuildingFactory2, label: "Manufacturing" },
  { id: "globe", Icon: IconWorld, label: "Global / export" },
  { id: "cpu", Icon: IconCpu, label: "Technology" },
  { id: "automotive", Icon: IconCar, label: "Automotive" },
  { id: "textile", Icon: IconShirt, label: "Textile / apparel" },
  { id: "energy", Icon: IconBolt, label: "Energy" },
  { id: "food", Icon: IconTruckDelivery, label: "Food / FMCG" },
  { id: "healthcare", Icon: IconHeartRateMonitor, label: "Healthcare" },
  { id: "maritime", Icon: IconShip, label: "Maritime / ports" },
  { id: "chemicals", Icon: IconFlask, label: "Chemicals" },
  { id: "cities", Icon: IconTopologyStar3, label: "Infrastructure / cities" },
  { id: "agriculture", Icon: IconTractor, label: "Agriculture" },
  { id: "mining", Icon: IconPick, label: "Mining / metals" },
  { id: "digital", Icon: IconCpu2, label: "Digital / enterprise tech" },
  { id: "aerospace", Icon: IconRocket, label: "Aerospace" },
  { id: "robotics", Icon: IconRobot, label: "Robotics" },
  { id: "automation", Icon: IconSettingsAutomation, label: "Automation" },
  { id: "ai", Icon: IconBrain, label: "AI / machine learning" },
  { id: "semiconductor", Icon: IconCpu2, label: "Semiconductors" },
  { id: "drone", Icon: IconDrone, label: "Drones / UAV" },
  { id: "satellite", Icon: IconSatellite, label: "Satellite / space" },
  { id: "defence", Icon: IconShield, label: "Defence / security" },
  { id: "finance", Icon: IconBuildingBank, label: "Finance / banking" },
  { id: "education", Icon: IconSchool, label: "Education" },
  { id: "oil-gas", Icon: IconGasStation, label: "Oil & gas" },
  { id: "hydrogen", Icon: IconFlame, label: "Hydrogen / fuels" },
  { id: "nuclear", Icon: IconAtom, label: "Nuclear / science" },
  { id: "water", Icon: IconDroplet, label: "Water / environment" },
  { id: "solar", Icon: IconSolarPanel2, label: "Solar" },
  { id: "wind", Icon: IconWind, label: "Wind energy" },
  { id: "ev", Icon: IconChargingPile, label: "EV / charging" },
  { id: "battery", Icon: IconBatteryAutomotive, label: "Batteries / storage" },
  { id: "telecom", Icon: IconAntenna, label: "Telecom" },
  { id: "iot", Icon: IconDevices, label: "IoT / devices" },
  { id: "cloud", Icon: IconCloudComputing, label: "Cloud computing" },
  { id: "data", Icon: IconDatabase, label: "Data / software" },
  { id: "cybersecurity", Icon: IconLock, label: "Cybersecurity" },
  { id: "logistics", Icon: IconPackage, label: "Logistics / warehousing" },
  { id: "rail", Icon: IconTrain, label: "Rail / transit" },
  { id: "aviation", Icon: IconPlane, label: "Aviation / travel" },
  { id: "construction", Icon: IconCrane, label: "Construction / EPC" },
  { id: "tools", Icon: IconHammer, label: "Tools / equipment" },
  { id: "packaging", Icon: IconBox, label: "Packaging" },
  { id: "print", Icon: IconPrinter, label: "Print / 3D print" },
  { id: "research", Icon: IconMicroscope, label: "Research / labs" },
  { id: "biotech", Icon: IconDna, label: "Biotech" },
  { id: "pharma", Icon: IconPill, label: "Pharma" },
  { id: "clinical", Icon: IconStethoscope, label: "Clinical / hospitals" },
  { id: "vaccines", Icon: IconVaccine, label: "Vaccines / life sciences" },
  { id: "veterinary", Icon: IconPaw, label: "Veterinary / animal" },
  { id: "forestry", Icon: IconTree, label: "Forestry / timber" },
  { id: "fishing", Icon: IconFish, label: "Fisheries / seafood" },
  { id: "waste", Icon: IconRecycle, label: "Waste / circular" },
  { id: "real-estate", Icon: IconBuildingSkyscraper, label: "Real estate" },
  { id: "hospitality", Icon: IconBuildingStore, label: "Hospitality / retail" },
  { id: "furniture", Icon: IconSofa, label: "Furniture / interiors" },
  { id: "jewelry", Icon: IconDiamond, label: "Jewelry / luxury" },
  { id: "media", Icon: IconDeviceTv, label: "Media / entertainment" },
  { id: "events", Icon: IconCalendarEvent, label: "Events / exhibitions" },
  { id: "marketing", Icon: IconSpeakerphone, label: "Marketing / advertising" },
  { id: "legal", Icon: IconScale, label: "Legal / compliance" },
  { id: "insurance", Icon: IconUmbrella, label: "Insurance" },
  { id: "consulting", Icon: IconBriefcase, label: "Consulting / services" },
  { id: "hr", Icon: IconUsers, label: "People / HR" },
  { id: "nonprofit", Icon: IconHeartHandshake, label: "Nonprofit / social" },
];

const EXTRA_FOCUS_SECTOR_ICONS: Record<string, TablerIcon> = Object.fromEntries(
  EXTRA_FOCUS_SECTOR_OPTIONS.map((option) => [option.id, option.Icon]),
);

/** Short ids stored in the DB — platform slugs are longer than the previous 40-char cap. */
const PLATFORM_SLUG_TO_SHORT_ID: Record<string, string> = {
  "automotive-mobility-ecosystem": "automotive",
  "industrial-automation-smart-manufacturing-ecosystem": "factory",
  "textile-apparel-fashion-manufacturing-ecosystem": "textile",
  "renewable-energy-electrification-ecosystem": "energy",
  "food-beverage-fmcg-ecosystem": "food",
  "healthcare-pharma-medtech-ecosystem": "healthcare",
  "maritime-ports-offshore-ecosystem": "maritime",
  "chemicals-materials-advanced-manufacturing-ecosystem": "chemicals",
  "infrastructure-construction-smart-cities-ecosystem": "cities",
  "agriculture-agritech-ecosystem": "agriculture",
  "mining-metals-resources-ecosystem": "mining",
  "digital-transformation-enterprise-technology-ecosystem": "digital",
  "aerospace-space-technology-ecosystem": "aerospace",
};

export function iconIdForPlatformSector(slug: string): string {
  return PLATFORM_SLUG_TO_SHORT_ID[slug] ?? slug;
}

/** Curated keys used in seed/custom focus sectors. Platform cards use the short ids above. */
export const FOCUS_SECTOR_ICON_OPTIONS: FocusSectorIconOption[] = EXTRA_FOCUS_SECTOR_OPTIONS;

function inferFocusSectorIconId(label: string): string | undefined {
  const text = label.trim().toLowerCase();
  if (!text) return undefined;
  if (/\b(robot|robotic|cobot)\b/.test(text)) return "robotics";
  if (/\b(ai|machine learning|genai|llm)\b/.test(text)) return "ai";
  if (/\b(drone|uav)\b/.test(text)) return "drone";
  if (/\b(defence|defense|military|security)\b/.test(text)) return "defence";
  if (/\b(manufactur|factory|industrial)\b/.test(text)) return "factory";
  if (/\b(export|partnership|trade|global|international)\b/.test(text)) return "globe";
  if (/\b(tech|collaborat|digital|software|cpu)\b/.test(text)) return "cpu";
  const extra = EXTRA_FOCUS_SECTOR_OPTIONS.find((option) => option.label.toLowerCase() === text);
  if (extra) return extra.id;
  const platform = HOME_SECTOR_CARDS.find((card) => card.title.toLowerCase() === text);
  return platform ? iconIdForPlatformSector(platform.slug) : undefined;
}

export function resolveFocusSectorIconId(icon?: string | null, label?: string | null): string | undefined {
  const trimmed = icon?.trim();
  if (trimmed) return trimmed;
  return label ? inferFocusSectorIconId(label) : undefined;
}

function iconFromStoredId(id: string): TablerIcon | undefined {
  if (id in EXTRA_FOCUS_SECTOR_ICONS) return EXTRA_FOCUS_SECTOR_ICONS[id];
  const shortId = PLATFORM_SLUG_TO_SHORT_ID[id];
  if (shortId && shortId in EXTRA_FOCUS_SECTOR_ICONS) return EXTRA_FOCUS_SECTOR_ICONS[shortId];
  if (id in sectorIconBySlug) return getSectorIcon(id);
  const truncatedSlug = Object.keys(sectorIconBySlug).find((slug) => slug.startsWith(id) && id.length >= 20);
  if (truncatedSlug) return getSectorIcon(truncatedSlug);
  return undefined;
}

export function getFocusSectorIcon(icon?: string | null, label?: string | null): TablerIcon {
  const id = resolveFocusSectorIconId(icon, label);
  if (!id) return IconSparkles;
  return iconFromStoredId(id) ?? IconSparkles;
}

const SHORT_ID_TO_PLATFORM_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(PLATFORM_SLUG_TO_SHORT_ID).map(([slug, shortId]) => [shortId, slug]),
);

/** Extra short ids that should share a platform color theme. */
const SHORT_ID_VISUAL_FALLBACK: Record<string, string> = {
  robotics: "industrial-automation-smart-manufacturing-ecosystem",
  automation: "industrial-automation-smart-manufacturing-ecosystem",
  factory: "industrial-automation-smart-manufacturing-ecosystem",
  ai: "digital-transformation-enterprise-technology-ecosystem",
  semiconductor: "digital-transformation-enterprise-technology-ecosystem",
  cpu: "digital-transformation-enterprise-technology-ecosystem",
  digital: "digital-transformation-enterprise-technology-ecosystem",
  cloud: "digital-transformation-enterprise-technology-ecosystem",
  data: "digital-transformation-enterprise-technology-ecosystem",
  iot: "digital-transformation-enterprise-technology-ecosystem",
  cybersecurity: "digital-transformation-enterprise-technology-ecosystem",
  telecom: "digital-transformation-enterprise-technology-ecosystem",
  ev: "automotive-mobility-ecosystem",
  battery: "renewable-energy-electrification-ecosystem",
  solar: "renewable-energy-electrification-ecosystem",
  wind: "renewable-energy-electrification-ecosystem",
  hydrogen: "renewable-energy-electrification-ecosystem",
  energy: "renewable-energy-electrification-ecosystem",
  automotive: "automotive-mobility-ecosystem",
  textile: "textile-apparel-fashion-manufacturing-ecosystem",
  food: "food-beverage-fmcg-ecosystem",
  healthcare: "healthcare-pharma-medtech-ecosystem",
  pharma: "healthcare-pharma-medtech-ecosystem",
  biotech: "healthcare-pharma-medtech-ecosystem",
  clinical: "healthcare-pharma-medtech-ecosystem",
  vaccines: "healthcare-pharma-medtech-ecosystem",
  maritime: "maritime-ports-offshore-ecosystem",
  chemicals: "chemicals-materials-advanced-manufacturing-ecosystem",
  cities: "infrastructure-construction-smart-cities-ecosystem",
  construction: "infrastructure-construction-smart-cities-ecosystem",
  agriculture: "agriculture-agritech-ecosystem",
  forestry: "agriculture-agritech-ecosystem",
  fishing: "agriculture-agritech-ecosystem",
  mining: "mining-metals-resources-ecosystem",
  aerospace: "aerospace-space-technology-ecosystem",
  drone: "aerospace-space-technology-ecosystem",
  satellite: "aerospace-space-technology-ecosystem",
};

export function resolveFocusSectorPlatformSlug(
  icon?: string | null,
  label?: string | null,
): string | null {
  const id = resolveFocusSectorIconId(icon, label);
  if (!id) return null;
  if (id in sectorIconBySlug) return id;
  if (SHORT_ID_TO_PLATFORM_SLUG[id]) return SHORT_ID_TO_PLATFORM_SLUG[id];
  if (SHORT_ID_VISUAL_FALLBACK[id]) return SHORT_ID_VISUAL_FALLBACK[id];
  const truncatedSlug = Object.keys(sectorIconBySlug).find((slug) => slug.startsWith(id) && id.length >= 20);
  return truncatedSlug ?? null;
}

export function getFocusSectorVisual(icon?: string | null, label?: string | null): SectorCardVisual {
  const slug = resolveFocusSectorPlatformSlug(icon, label);
  return slug ? getSectorVisual(slug) : defaultSectorVisual;
}

/** Visual + icon for corridor expertise chip (industry-driven, tag as label). */
export function getExpertiseChipVisual(industry?: string | null): SectorCardVisual {
  const slug = getSectorSlugByIndustry(industry);
  return slug ? getSectorVisual(slug) : defaultSectorVisual;
}

export function getExpertiseChipIcon(industry?: string | null, tag?: string | null): TablerIcon {
  const slug = getSectorSlugByIndustry(industry);
  if (slug) return getSectorIcon(slug);
  return getFocusSectorIcon(undefined, tag ?? industry);
}

export function getFocusSectorHref(icon?: string | null): string | null {
  const id = icon?.trim();
  if (!id) return null;
  const card = HOME_SECTOR_CARDS.find((sector) => {
    if (sector.comingSoon) return false;
    if (sector.slug === id) return true;
    if (PLATFORM_SLUG_TO_SHORT_ID[sector.slug] === id) return true;
    return sector.slug.startsWith(id) && id.length >= 20;
  });
  return card ? getSectorHref(card.slug) : null;
}
